import { aiService } from '../../services/aiService';
import { 
  TRANSLATION_INSTRUCTIONS, 
  NAME_TRANSLATION_INSTRUCTIONS,
  DESCRIPTION_TRANSLATION_INSTRUCTIONS,
  SIMPLE_NAME_TRANSLATION_INSTRUCTION,
  NAME_VALIDATION_INSTRUCTION,
  validateTranslation,
  validateLanguageSpecific,
  getModelSettings,
  getOptimalTokenLimit,
  sanitizeTranslatedJson
} from '../../constants/translationConfig';
import { initialPoiState } from './utils'; // Import initialPoiState for structure reference

/**
 * Enhanced name translation with multiple fallback strategies and validation
 */
const translateNameWithFallbacks = async (name, targetLanguage, translationModel) => {
  if (!name || name.trim() === '') {
    return name;
  }

  console.log(`Translating name: "${name}"`);
  
  // Define fallback strategies in order of preference
  const strategies = [
    {
      instruction: NAME_TRANSLATION_INSTRUCTIONS + `\n\nOriginal: "${name}"\nTranslated name:`,
      name: 'detailed'
    },
    {
      instruction: NAME_VALIDATION_INSTRUCTION + `\n\nOriginal name: "${name}"\nTranslated name (ONLY):`,
      name: 'validation'
    },
    {
      instruction: `Translate ONLY this title to ${targetLanguage}: "${name}". Return ONLY the translated title.`,
      name: 'simple'
    },
    {
      instruction: SIMPLE_NAME_TRANSLATION_INSTRUCTION,
      name: 'minimal'
    }
  ];

  const modelSettings = getModelSettings(translationModel);
  
  for (let i = 0; i < strategies.length; i++) {
    const strategy = strategies[i];
    
    try {
      console.log(`Attempting name translation with ${strategy.name} strategy`);
      
      // Use conservative token limits for names
      const options = {
        maxTokens: Math.min(512, modelSettings.maxTokens),
        temperature: 0.1 // Very low temperature for consistent name translations
      };

      let translatedName = await aiService.translateText(
        name,
        targetLanguage,
        translationModel,
        strategy.instruction
      );

      // Clean the result immediately
      translatedName = translatedName.replace(/^["']|["']$/g, '').trim();
      
      // Enhanced validation checks
      const isValid = validateNameTranslation(name, translatedName, targetLanguage);
      
      if (isValid) {
        console.log(`✓ Name translation successful with ${strategy.name} strategy: "${translatedName}"`);
        return translatedName;
      } else {
        console.warn(`Name translation with ${strategy.name} strategy failed validation, trying next strategy`);
        continue;
      }
      
    } catch (error) {
      console.warn(`Name translation with ${strategy.name} strategy failed:`, error.message);
      
      // If this is the last strategy, fall back to original
      if (i === strategies.length - 1) {
        console.error('All name translation attempts failed, using original name');
        return name;
      }
      continue;
    }
  }
  
  return name; // Fallback to original if all strategies fail
};

/**
 * Validates a name translation result
 */
const validateNameTranslation = (original, translated, targetLanguage) => {
  // Basic validation
  if (!translated || translated.trim().length === 0) {
    return false;
  }

  // Length check - translations shouldn't be dramatically longer
  if (translated.length > original.length * 3) {
    console.warn('Translated name is much longer than original');
    return false;
  }

  // Content validation - should not contain unwanted elements
  const invalidPatterns = [
    /\n/,           // No line breaks
    /```/,          // No code blocks
    /^(here is|voici|aquí|aqui)/i,  // No explanatory text
    /^(the translation|la traduction|la traducción)/i
  ];

  for (const pattern of invalidPatterns) {
    if (pattern.test(translated)) {
      console.warn('Translated name contains invalid content');
      return false;
    }
  }

  // Length limit for names
  if (translated.length > 200) {
    console.warn('Translated name is too long');
    return false;
  }

  // Language-specific validation
  try {
    validateLanguageSpecific(translated, targetLanguage);
  } catch (error) {
    console.warn('Language-specific validation failed:', error.message);
    return false;
  }

  return true;
};

/**
 * Enhanced field translation with better error handling and validation
 */
const translateField = async (text, targetLanguage, translationModel, fieldType = 'general') => {
  if (!text || (typeof text === 'string' && text.trim() === '')) {
    return text;
  }

  try {
    // Choose appropriate instruction based on field type
    let instruction = TRANSLATION_INSTRUCTIONS;
    if (fieldType === 'description' || fieldType === 'inDepthDescription') {
      instruction = DESCRIPTION_TRANSLATION_INSTRUCTIONS;
    }

    const modelSettings = getModelSettings(translationModel);
    const optimalTokenLimit = getOptimalTokenLimit(text, translationModel);
    
    const options = {
      maxTokens: optimalTokenLimit,
      temperature: modelSettings.temperature
    };

    console.log(`Translating ${fieldType} field (${text.length} chars)`);
    
    const result = await aiService.translateText(
      text,
      targetLanguage,
      translationModel,
      instruction
    );

    // Validate the translation
    validateTranslation(text, result, targetLanguage);
    validateLanguageSpecific(result, targetLanguage);

    console.log(`✓ Successfully translated ${fieldType} field`);
    return result;

  } catch (error) {
    console.error(`Translation failed for ${fieldType} field:`, error);
    return text; // Return original text as fallback
  }
};

const mergeTranslatedPoiText = (poi, translatedContent, targetLanguage) => ({
  ...poi,
  ...translatedContent,
  nextStop: translatedContent.nextStop
    ? { ...poi.nextStop, ...translatedContent.nextStop }
    : poi.nextStop,
  flexibleContent: translatedContent.flexibleContent
    ? { ...poi.flexibleContent, ...translatedContent.flexibleContent }
    : poi.flexibleContent,
  language: targetLanguage
});

export const stripTranslatedPoiAudioFields = (poi) => ({
  ...poi,
  audioUrl: '',
  uncompressedAudioUrl: '',
  originalAudioUrl: '',
  compressedAudioVariants: [],
  audioGeneratedAt: '',
  audioModel: ''
});

/**
 * Translates the translatable fields of a single POI object with enhanced error handling.
 *
 * @param {object} poi - The POI object to translate.
 * @param {string} targetLanguage - The language code to translate to (e.g., 'es', 'fr').
 * @param {string} translationModel - The AI model to use for translation.
 * @param {function} onProgress - Optional progress callback function.
 * @returns {Promise<object>} A new POI object with translated fields.
 */
 export const translatePoiFields = async (poi, targetLanguage, translationModel, onProgress) => {
  try {
    if (onProgress) onProgress('Starting bulk POI translation...');
    
    // Create the translatable object (only fields that need translation)
    const translatablePoi = {
      name: poi.name || '',
      description: poi.description || '',
      inDepthDescription: poi.inDepthDescription || '',
      category: poi.category || '',
      openingHours: poi.openingHours || '',
      entryFee: poi.entryFee || '',
      tips: poi.tips || '',
      orientationInstructions: poi.orientationInstructions || '',
      accessibility: poi.accessibility || '',
      bestTimeToVisit: poi.bestTimeToVisit || '',
      historicalContext: poi.historicalContext || '',
      culturalSignificance: poi.culturalSignificance || '',
      hiddenGems: poi.hiddenGems || '',
      thematicConnections: poi.thematicConnections || [],
      visitorExperiences: poi.visitorExperiences || [],
      historicalNarrative: poi.historicalNarrative || '',
      keywords: poi.keywords || [],
      flexibleContent: {
        title: poi.flexibleContent?.title || '',
        content: poi.flexibleContent?.content || ''
      },
      nextStop: {
        description: poi.nextStop?.description || '',
        transportOptions: poi.nextStop?.transportOptions || []
      }
    };

    // Remove empty fields to save tokens
    Object.keys(translatablePoi).forEach(key => {
      if (key === 'nextStop') {
        if (!translatablePoi.nextStop.description && translatablePoi.nextStop.transportOptions.length === 0) {
          delete translatablePoi.nextStop;
        }
      } else if (key === 'flexibleContent') {
        if (!translatablePoi.flexibleContent.title && !translatablePoi.flexibleContent.content) {
          delete translatablePoi.flexibleContent;
        }
      } else if (Array.isArray(translatablePoi[key])) {
        if (translatablePoi[key].length === 0) delete translatablePoi[key];
      } else if (!translatablePoi[key] || translatablePoi[key].trim() === '') {
        delete translatablePoi[key];
      }
    });

    console.log(`Bulk translating POI: "${poi.name}" to ${targetLanguage}`);
    
    // Single API call instead of 8+ calls
    const messages = [
      {
        role: 'system',
        content: `${TRANSLATION_INSTRUCTIONS}

Target language: ${targetLanguage}
Translate all text values in this POI JSON object. Return ONLY valid JSON with the same structure.`
      },
      {
        role: 'user',
        content: JSON.stringify(translatablePoi, null, 2)
      }
    ];

    const translatedJson = await aiService.getCompletion(messages, translationModel, {
      maxTokens: 4096,
      temperature: 0.2
    });

    // Parse and merge the result
    const cleanJson = translatedJson.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const translatedContent = sanitizeTranslatedJson(JSON.parse(cleanJson));

    // Merge back into original POI structure
    const result = stripTranslatedPoiAudioFields(mergeTranslatedPoiText(poi, translatedContent, targetLanguage));

    console.log(`✅ Bulk translation successful: "${poi.name}" → "${result.name}"`);
    if (onProgress) onProgress('Bulk translation complete');
    
    return result;

  } catch (error) {
    console.error('Bulk translation failed:', error);
    
    // Simple fallback - just return original POI with updated language
    console.warn('Returning original POI as fallback');
    return stripTranslatedPoiAudioFields({
      ...poi,
      language: targetLanguage
    });
  }
};

/**
 * Batch translate multiple POIs with enhanced progress tracking and error recovery
 */

export const translateMultiplePois = async (pois, targetLanguage, translationModel, onProgress) => {
  try {
    if (pois.length === 0) return [];
    if (pois.length === 1) return [await translatePoiFields(pois[0], targetLanguage, translationModel, onProgress)];

    if (onProgress) onProgress(`Bulk translating ${pois.length} POIs...`);

    // Create array of translatable POI objects
    const translatablePois = pois.map(poi => ({
      name: poi.name || '',
      description: poi.description || '',
      inDepthDescription: poi.inDepthDescription || '',
      category: poi.category || '',
      openingHours: poi.openingHours || '',
      entryFee: poi.entryFee || '',
      tips: poi.tips || '',
      orientationInstructions: poi.orientationInstructions || '',
      accessibility: poi.accessibility || '',
      bestTimeToVisit: poi.bestTimeToVisit || '',
      historicalContext: poi.historicalContext || '',
      culturalSignificance: poi.culturalSignificance || '',
      hiddenGems: poi.hiddenGems || '',
      thematicConnections: poi.thematicConnections || [],
      visitorExperiences: poi.visitorExperiences || [],
      historicalNarrative: poi.historicalNarrative || '',
      keywords: poi.keywords || [],
      flexibleContent: {
        title: poi.flexibleContent?.title || '',
        content: poi.flexibleContent?.content || ''
      },
      nextStop: {
        description: poi.nextStop?.description || '',
        transportOptions: poi.nextStop?.transportOptions || []
      }
    }));

    console.log(`Bulk translating ${pois.length} POIs in single API call`);

    // Single API call for ALL POIs
    const messages = [
      {
        role: 'system', 
        content: `${TRANSLATION_INSTRUCTIONS}

Target language: ${targetLanguage}
Translate all text values in this array of POI JSON objects. Return ONLY a valid JSON array with the same structure and order.`
      },
      {
        role: 'user',
        content: JSON.stringify(translatablePois, null, 2)
      }
    ];

    const translatedJson = await aiService.getCompletion(messages, translationModel, {
      maxTokens: 8192, // More tokens for multiple POIs
      temperature: 0.2
    });

    // Parse results
    const cleanJson = translatedJson.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    const translatedContents = sanitizeTranslatedJson(JSON.parse(cleanJson));

    // Merge back into original POI structures
    const results = pois.map((poi, index) => (
      stripTranslatedPoiAudioFields(mergeTranslatedPoiText(poi, translatedContents[index] || {}, targetLanguage))
    ));

    console.log(`✅ Bulk translation successful for ${pois.length} POIs`);
    if (onProgress) onProgress('Bulk translation complete');

    return results;

  } catch (error) {
    console.error('Bulk multiple POI translation failed, falling back to individual:', error);
    
    // Fallback to individual POI translation
    const results = [];
    for (let i = 0; i < pois.length; i++) {
      const result = await translatePoiFields(pois[i], targetLanguage, translationModel, 
        (msg) => onProgress && onProgress(`[${i+1}/${pois.length}] ${msg}`)
      );
      results.push(result);
    }
    return results;
  }
};
 
export const translatePoisBatch = async (pois, targetLanguage, translationModel, onProgress) => {
  // Use the new bulk approach instead of field-by-field
  return await translateMultiplePois(pois, targetLanguage, translationModel, onProgress);
};

/**
 * Estimate translation time based on POI content complexity
 */
export const estimatePoisTranslationTime = (pois) => {
  const baseTimePerField = 3; // seconds per field
  const fieldsPerPoi = 7; // name + 6 other fields on average
  
  // Calculate total content length for complexity estimation
  const totalContentLength = pois.reduce((total, poi) => {
    return total + [
      poi.name || '',
      poi.description || '',
      poi.inDepthDescription || '',
      poi.historicalContext || '',
      poi.culturalSignificance || '',
      poi.hiddenGems || '',
      poi.nextStop?.description || ''
    ].join('').length;
  }, 0);
  
  // Complexity multiplier based on content length
  const complexityMultiplier = totalContentLength > 10000 ? 1.8 : 
                              totalContentLength > 5000 ? 1.4 : 1.0;
  
  const estimatedSeconds = pois.length * fieldsPerPoi * baseTimePerField * complexityMultiplier;
  
  return {
    seconds: Math.ceil(estimatedSeconds),
    minutes: Math.ceil(estimatedSeconds / 60),
    complexity: complexityMultiplier > 1.5 ? 'high' : complexityMultiplier > 1.2 ? 'medium' : 'low'
  };
};

/**
 * Validate translation completeness for a batch of POIs
 */
export const validatePoisTranslationCompleteness = (originalPois, translatedPois, targetLanguage) => {
  const issues = [];
  const summary = {
    total: originalPois.length,
    successful: 0,
    partiallyTranslated: 0,
    failed: 0,
    issues: []
  };
  
  if (originalPois.length !== translatedPois.length) {
    issues.push('POI count mismatch');
    summary.failed = originalPois.length;
    return { ...summary, issues };
  }
  
  originalPois.forEach((originalPoi, index) => {
    const translatedPoi = translatedPois[index];
    const poiIssues = [];
    
    // Check name translation
    if (originalPoi.name && originalPoi.name === translatedPoi.name && originalPoi.name.length > 5) {
      poiIssues.push('name not translated');
    }
    
    // Check description translation
    if (originalPoi.description && originalPoi.description === translatedPoi.description && originalPoi.description.length > 10) {
      poiIssues.push('description not translated');
    }
    
    // Check language field
    if (translatedPoi.language !== targetLanguage) {
      poiIssues.push('language field not updated');
    }
    
    if (poiIssues.length === 0) {
      summary.successful++;
    } else if (poiIssues.length < 3) {
      summary.partiallyTranslated++;
      issues.push(`POI ${index + 1} (${originalPoi.name}): ${poiIssues.join(', ')}`);
    } else {
      summary.failed++;
      issues.push(`POI ${index + 1} (${originalPoi.name}): translation failed`);
    }
  });
  
  return { ...summary, issues };
};

/**
 * Create detailed translation summary for logging and debugging
 */
export const createPoisTranslationSummary = (originalPois, translatedPois, targetLanguage, duration, model) => {
  const validation = validatePoisTranslationCompleteness(originalPois, translatedPois, targetLanguage);
  const estimate = estimatePoisTranslationTime(originalPois);
  
  return {
    timestamp: new Date().toISOString(),
    targetLanguage,
    model,
    duration: `${duration}ms`,
    performance: {
      estimated: `${estimate.seconds}s`,
      actual: `${Math.round(duration / 1000)}s`,
      efficiency: Math.round((estimate.seconds / (duration / 1000)) * 100) + '%'
    },
    results: {
      total: validation.total,
      successful: validation.successful,
      partiallyTranslated: validation.partiallyTranslated,
      failed: validation.failed,
      successRate: Math.round((validation.successful / validation.total) * 100) + '%'
    },
    complexity: estimate.complexity,
    issues: validation.issues.slice(0, 5), // Limit to first 5 issues for readability
    recommendations: generateRecommendations(validation, estimate, duration)
  };
};

/**
 * Generate recommendations based on translation results
 */
const generateRecommendations = (validation, estimate, actualDuration) => {
  const recommendations = [];
  
  if (validation.failed > 0) {
    recommendations.push('Consider using a more reliable translation model');
  }
  
  if (validation.partiallyTranslated > validation.successful) {
    recommendations.push('Review translation instructions for better consistency');
  }
  
  if (actualDuration > estimate.seconds * 1000 * 2) {
    recommendations.push('Consider using a faster model or implementing parallel processing');
  }
  
  if (estimate.complexity === 'high') {
    recommendations.push('Break down large translations into smaller batches');
  }
  
  return recommendations;
};

// Export all functions for use in other modules
export default {
  translatePoiFields,
  translatePoisBatch,
  estimatePoisTranslationTime,
  validatePoisTranslationCompleteness,
  createPoisTranslationSummary
};
